import React, { useCallback, useContext, useState } from 'react';
import styled, { ThemeContext } from 'styled-components';
import { getAppTitle, Headline3, Notifications, NotificationType, SIDE_NAV_WIDTH, TOP_NAV_HEIGHT } from 'venn-ui-kit';
import { Modal, ModalSize } from 'venn-components';
import { requestVennFeatureInfo } from 'venn-api';
import {
  UpsellBackButton,
  UpsellContactFormContainer,
  UpsellDescriptionSpan,
  UpsellFeature,
  UpsellFooter,
  UpsellFooterText,
  UpsellFurtherContactButton,
  UpsellIconWithMargin,
  UpsellMainTextContainer,
  UpsellSecondPageContainer,
  UpsellSecondPageLeftContainer,
  UpsellStyledButton,
  UpsellStyledInput,
  TitleTag,
  UpsellImage,
  type UpsellFeatureProps,
  type UpsellProps,
} from './UpsellFlyoutShared';
import { analyticsService } from 'venn-utils';

const FIRST_PAGE_INDEX = 0;
const SECOND_PAGE_INDEX = 1;

const featuresConfig: UpsellFeatureProps[] = [
  {
    title: 'Sector, Geography, and Asset Class breakdown',
    description: ' ',
  },
  {
    title: 'Top 10 Holdings',
    description: ' ',
  },
  {
    title: 'Credit Quality, Effective Duration, and Maturity Breakdown',
    description: ' ',
  },
  {
    title: '30+ Financial Performance Indicators including:',
    description: 'Fixed Income metrics, Equity Valuations, Yields, Style, Market Capitalization, and Expense Ratio',
  },
];

export const InvestmentSnapshotUpsell = ({ onClose }: UpsellProps) => {
  const { Images } = useContext(ThemeContext);
  const [page, setPage] = useState(FIRST_PAGE_INDEX);
  const [additionalText, setAdditionalText] = useState('');

  const onAdditionalTextChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setAdditionalText(e.target.value);
    },
    [setAdditionalText],
  );

  const onContactUs = useCallback(async () => {
    setPage(SECOND_PAGE_INDEX);
    await requestVennFeatureInfo({
      featureName: 'Investment Snapshot',
      message: '',
    });
    analyticsService.ctaClicked({
      purpose: 'Investment Snapshot Info Requested',
      locationOnPage: 'Investment Snapshot Upsell Flyout',
    });
    Notifications.notify('You have successfully submitted a request for more information.', NotificationType.SUCCESS);
  }, [setPage]);

  const onAdditionalContact = useCallback(async () => {
    await requestVennFeatureInfo({
      featureName: 'Investment Snapshot',
      message: additionalText,
    });
    onClose();
    analyticsService.ctaClicked({
      purpose: 'Investment Snapshot Request Additional Info Added',
      locationOnPage: 'Investment Snapshot Upsell Flyout',
    });
    Notifications.notify('You have successfully submitted a request for more information.', NotificationType.SUCCESS);
  }, [additionalText, onClose]);

  return (
    <UpsellStyledModal closeOnEsc closeOnOutsideClick size={ModalSize.Large} onClose={onClose} noPadding>
      {page === 0 && (
        <div className="flex flex-col justify-start items-start gap-[16px] pt-[54px] px-[40px] pb-[28px] bg-venn-white">
          <div className=" flex flex-row space-between w-full gap-[20px]">
            <div className="flex flex-col max-w-[416px]">
              <HeadlineContainer>
                <Headline3>Introducing {getAppTitle()}'s Investment Snapshot</Headline3>
                <TitleTag>NEW</TitleTag>
              </HeadlineContainer>
              <UpsellMainTextContainer>
                {getAppTitle()}’s Investment Snapshot offers new ways to understand underlying investment
                characteristics and risks, including the ability to see holdings-derived fundamental data and financial
                performance metrics.
              </UpsellMainTextContainer>
              {featuresConfig.map((props) => (
                <UpsellFeature key={props.title} {...props} />
              ))}
            </div>
            <UpsellImage
              src={Images.investmentSnapshotSlideoutAsset ?? ''}
              alt="investment-snapshot"
              width={568}
              height={350}
            />
          </div>
          <UpsellFooter>
            <UpsellFooterText>Interested in learning more? Submit a request and we’ll be in touch.</UpsellFooterText>
            <UpsellStyledButton dense onClick={onContactUs} className="portal-menu-ignore-activity">
              Submit Request
            </UpsellStyledButton>
          </UpsellFooter>
        </div>
      )}
      {page === 1 && (
        <UpsellSecondPageContainer>
          <UpsellSecondPageLeftContainer>
            <UpsellBackButton dense onClick={() => setPage(FIRST_PAGE_INDEX)} className="portal-menu-ignore-activity">
              <UpsellIconWithMargin type="arrow-left" />
              Back
            </UpsellBackButton>
            <UpsellImage
              src={Images.investmentSnapshotSlideoutAsset ?? ''}
              alt="investment-snapshot"
              width={400}
              height={246}
              className="my-auto"
            />
          </UpsellSecondPageLeftContainer>
          <UpsellContactFormContainer>
            <Headline3>Thanks for getting in touch.</Headline3>
            <UpsellDescriptionSpan>
              <b>Is there anything else you would like us to know? (optional)</b>
            </UpsellDescriptionSpan>
            <UpsellStyledInput
              placeholder="Enter here"
              defaultValue=""
              onChange={onAdditionalTextChange}
              minLength={1}
              maxLength={500}
            />
            <UpsellFurtherContactButton dense onClick={onAdditionalContact} className="portal-menu-ignore-activity">
              Send
            </UpsellFurtherContactButton>
          </UpsellContactFormContainer>
        </UpsellSecondPageContainer>
      )}
    </UpsellStyledModal>
  );
};

const UpsellStyledModal = styled(Modal)`
  &.qa-modal-overlay {
    top: ${TOP_NAV_HEIGHT}px;
    left: ${SIDE_NAV_WIDTH}px;
    justify-content: flex-start;
    align-items: flex-start;
    padding-top: 65px;
  }
  &.modal-wrapper {
    width: 1088px;
    height: 475px;
  }

  @media print {
    display: none;
  }
`;

const HeadlineContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
`;
